import { useParams } from 'react-router-dom'
import { Banner } from '@/ui'
import { useLotBalance } from '../../api/recall'
import type { LotBalanceRow } from '../../model/recall/mappers'
import { accountedTone, balanceSegments, flagLabel, pctText, seasonLabel, type SegmentKey } from '../../model/recall/lotBalance'
import { ConsolePage, DetailColumns, QueryState } from '../../components/Layout'
import { Code, Facts, Kg, RefLink, Section, StatTile, Status, UserName } from '../../components/bits'
import { streamLabel } from '../../model/recall/words'

const SEGMENT_CLASS: Record<SegmentKey, string> = {
  inBags: 'bg-muted/50',
  brandBatch: 'bg-amber',
  convBatch: 'bg-reserve',
  byproduct: 'bg-reserve/50',
  waste: 'bg-ink/40',
  packedWaiting: 'bg-info/50',
  packedReleased: 'bg-ok',
  packedHeld: 'bg-warn',
  packedDestroyed: 'bg-danger/60',
  elsewhere: 'bg-amber/40',
  lost: 'bg-danger',
}

/** One lot's balance — F7.9: where each kilo bought at the gate went. */
export function LotBalanceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const q = useLotBalance(id)
  const b = q.data
  return (
    <ConsolePage
      back={{ to: '/c/lot-balance', label: 'Lot balance' }}
      eyebrow="Lot balance"
      title={b ? <Code className="text-[inherit]">{b.lotCode}</Code> : 'Lot balance'}
      description={b ? `${seasonLabel(b.seasonCode)} · ${streamLabel(b.stream)}` : undefined}
      actions={b ? <RefLink to={`/c/lots/${b.lotId}`} className="font-sans text-[14px]">Open the lot</RefLink> : undefined}
    >
      <QueryState query={q} label="this lot’s balance">
        {b && <BalanceBody b={b} />}
      </QueryState>
    </ConsolePage>
  )
}

function BalanceBody({ b }: { b: LotBalanceRow }) {
  const segments = balanceSegments(b)
  const tone = accountedTone(b)
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile label="Bought at the gate" value={<Kg grams={b.gateNetG} className="text-[inherit]" />} />
        <StatTile
          label="Accounted for"
          value={pctText(b.accountedPct)}
          tone={tone === 'neutral' ? 'neutral' : tone}
          hint={
            <>
              <Kg grams={b.accountedG} className="text-[inherit]" /> · line {b.accountedMinPct} %
            </>
          }
        />
        <StatTile label="Not accounted for" value={<Kg grams={b.lostG} className="text-[inherit]" />} tone={b.lostG > 0 && tone === 'danger' ? 'danger' : 'neutral'} />
        <StatTile
          label="Runs still open"
          value={b.openRuns}
          tone={b.openRuns > 0 ? 'warn' : 'neutral'}
          hint={b.openRuns > 0 ? 'The share is not final until they close' : undefined}
        />
      </div>

      {b.flagReasons.length > 0 && (
        <Banner tone="error" title="Flagged for the central team to explain">
          <ul className="mt-1 list-disc pl-5 text-ink">
            {b.flagReasons.map((f) => (
              <li key={f}>{flagLabel(f, b.accountedMinPct)}</li>
            ))}
          </ul>
        </Banner>
      )}

      <DetailColumns
        main={
          <Section title="Where the kilos went" eyebrow="Breakdown">
            {segments.length === 0 ? (
              <p className="text-[14px] text-muted">Nothing recorded for this lot yet.</p>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="flex h-6 w-full overflow-hidden rounded-chip border border-rule bg-paper-sunk" role="img" aria-label="Share of the gate kilos by where they went">
                  {segments.map((s) => (
                    <div key={s.key} className={SEGMENT_CLASS[s.key]} style={{ width: `${s.pct}%` }} title={`${s.label}: ${s.pct.toFixed(1)} %`} />
                  ))}
                </div>
                <table className="w-full text-[14px]">
                  <caption className="sr-only">Where the kilos went</caption>
                  <thead>
                    <tr className="text-left font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                      <th scope="col" className="py-1.5">Where</th>
                      <th scope="col" className="py-1.5 text-right">Kilos</th>
                      <th scope="col" className="py-1.5 text-right">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {segments.map((s) => (
                      <tr key={s.key} className="border-t border-rule/60">
                        <td className="py-1.5">
                          <span className="inline-flex items-center gap-2">
                            <span aria-hidden="true" className={`inline-block h-3 w-3 rounded-chip ${SEGMENT_CLASS[s.key]}`} />
                            <span className={s.key === 'lost' ? 'text-danger' : undefined}>{s.label}</span>
                          </span>
                        </td>
                        <td className="py-1.5 text-right">
                          <Kg grams={s.grams} />
                        </td>
                        <td className="py-1.5 text-right font-mono tabular">{s.pct.toFixed(1)} %</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-rule font-semibold">
                      <td className="py-1.5">Bought at the gate</td>
                      <td className="py-1.5 text-right">
                        <Kg grams={b.gateNetG} />
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Section>
        }
        side={
          <Section title="Along the way">
            <Facts
              columns={2}
              items={[
                { label: 'Gate net', value: <Kg grams={b.gateNetG} /> },
                { label: 'Received at the store', value: <Kg grams={b.receivedG} /> },
                { label: 'Sent to the machines', value: <Kg grams={b.issuedG} /> },
                { label: 'Still in sacks', value: <Kg grams={b.inLotBagsG} /> },
                { label: 'Brand packed', value: <Kg grams={b.brandPackedG} /> },
                { label: 'Farmer', value: <UserName id={b.farmerId} /> },
                { label: 'Lot state', value: <Status status={b.status} /> },
                { label: 'Hold', value: <Status status={b.hold} /> },
              ]}
            />
          </Section>
        }
      />
    </>
  )
}
